// @ts-nocheck
// "Embed" snippet modal (YouTube/Calendly-style): shows the copy-paste iframe
// snippet for the embedded single-page registration, with one-click Copy and a
// preview link. See apps/docs/content/guides/embed-registration.mdx — keep the
// snippet here and in the docs in sync.
import React, { useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Copy, ExternalLink } from 'lucide-react';
import { tenantOrigin, localTenantOrigin } from '@staty/shared/domains';

// Where the embed page lives. Local dev: the public app resolves the org by
// query/default on localhost and the embed page is registration-id driven, so a
// plain localhost URL works for testing. Production: the org's subdomain.
function buildEmbedUrl(orgSlug: string, registrationId: string) {
  const { hostname } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
  return isLocal
    ? `${localTenantOrigin()}/embed/register/${registrationId}`
    : `${tenantOrigin(orgSlug)}/embed/register/${registrationId}`;
}

function buildSnippet(embedUrl: string) {
  return `<iframe id="staty-register"
  src="${embedUrl}"
  style="width:100%;border:0" frameborder="0"
  allow="payment *"></iframe>
<script>
  window.addEventListener('message', function (e) {
    if (e.data && e.data.type === 'staty:height') {
      document.getElementById('staty-register').style.height = (e.data.value + 20) + 'px';
    }
  });
</script>`;
}

export default function EmbedCodeModal({ open, onOpenChange, registrationId, orgSlug }) {
  const embedUrl = useMemo(
    () => (registrationId ? buildEmbedUrl(orgSlug || '', registrationId) : ''),
    [orgSlug, registrationId],
  );
  const snippet = useMemo(() => buildSnippet(embedUrl), [embedUrl]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success('Embed code copied to clipboard.');
    } catch (e) {
      toast.error('Could not copy. Select the code and copy manually.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Embed this registration</DialogTitle>
          <DialogDescription>
            Paste this into your website&apos;s HTML where you want the registration form to
            appear. Players register (and pay) right on your site. Teams still show up here.
          </DialogDescription>
        </DialogHeader>

        <pre
          className="max-h-64 overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-all cursor-text select-all"
          onClick={(e) => {
            // Select-all on click, like most embed dialogs.
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }}
        >{snippet}</pre>

        <p className="text-xs text-muted-foreground">
          Tip: match your site&apos;s brand color by appending <code className="font-mono">?primary=1d4ed8</code> (any
          hex, no #) to the iframe URL.
        </p>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" asChild>
            <a href={embedUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1 h-4 w-4" /> Open preview
            </a>
          </Button>
          <Button onClick={copy}>
            <Copy className="mr-1 h-4 w-4" /> Copy code
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
